import { createFileRoute } from "@tanstack/react-router";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { openCookiePreferences } from "@/lib/consent";

const TITLE = "Política de privacidade | Nook Gym";
const DESCRIPTION =
  "Como a Nook Gym coleta, utiliza, armazena, compartilha e protege dados pessoais, nos termos da LGPD.";
const LAST_UPDATE = "18/08/2026";

export const Route = createFileRoute("/politica-de-privacidade")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: "https://nookgym.com.br/politica-de-privacidade" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "robots", content: "index, follow" },
    ],
    links: [{ rel: "canonical", href: "https://nookgym.com.br/politica-de-privacidade" }],
  }),
  component: Page,
});

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h2 className="text-base font-semibold text-foreground">{title}</h2>
      <div className="mt-3 space-y-3">{children}</div>
    </div>
  );
}

function List({ items }: { items: string[] }) {
  return (
    <ul className="list-disc space-y-2 pl-5">
      {items.map((i) => (
        <li key={i}>{i}</li>
      ))}
    </ul>
  );
}

function Page() {
  return (
    <section className="pb-20 pt-28">
      <div className="mx-auto max-w-3xl px-5 lg:px-8">
        <Breadcrumbs items={[{ label: "Início", to: "/" }, { label: "Política de privacidade" }]} />
        <h1 className="display mt-8 text-4xl sm:text-5xl">Política de privacidade</h1>
        <p className="mt-4 text-xs uppercase tracking-[0.16em] text-muted-foreground">
          Última atualização: {LAST_UPDATE}
        </p>

        <div className="mt-10 space-y-8 text-sm leading-relaxed text-muted-foreground">
          <p>
            Esta Política tem por objetivo explicar como a Nook Gym coleta, utiliza, armazena,
            compartilha e protege dados pessoais no desenvolvimento de suas atividades, aplicando-se,
            conforme o caso, ao site institucional, à página de agendamento de treino experimental,
            aos canais de atendimento, à contratação e à utilização dos serviços.
          </p>
          <p>
            O tratamento de dados pessoais observa a Lei nº 13.709/2018 (LGPD) e demais normas
            aplicáveis.
          </p>

          <Section title="1. Quem somos e qual é o nosso papel">
            <p>
              A NOOK GYM ACADEMIA LTDA atua como controladora dos dados pessoais tratados nas
              hipóteses desta Política.
            </p>
            <p>
              Controlador: NOOK GYM ACADEMIA LTDA | CNPJ: 55.440.324/0001-32 | Endereço: Rua Luiza
              Nogueira Gonçalves Prof., nº 00350, Blc 1, Lojas 102/103/121/122, Recreio dos
              Bandeirantes, Rio de Janeiro/RJ, CEP 22.790-465 | E-mail de privacidade:{" "}
              <a href="mailto:contato@nookgym.com.br" className="text-nook-green hover:underline">
                contato@nookgym.com.br
              </a>
              .
            </p>
            <p>
              Empresas contratadas pela Nook Gym para atividades específicas poderão atuar como
              operadoras, sob suas instruções e a legislação aplicável.
            </p>
          </Section>

          <Section title="2. A quem esta Política se aplica">
            <List
              items={[
                "Visitantes e usuários do site",
                "Pessoas que solicitem informações sobre os serviços",
                "Pessoas que agendem treino experimental",
                "Potenciais alunos",
                "Alunos e ex-alunos",
                "Responsáveis legais, quando aplicável",
                "Pessoas que entrem em contato com a Nook Gym",
                "Fornecedores, parceiros e prestadores de serviços, quando aplicável",
              ]}
            />
          </Section>

          <Section title="3. Quais dados pessoais podem ser tratados">
            <p>
              3.1. Dados cadastrais e de identificação: nome completo, CPF, data de nascimento,
              endereço, telefone, WhatsApp, e-mail, unidade de preferência.
            </p>
            <p>
              3.2. Dados de contratação e utilização dos serviços: plano contratado, unidade, data de
              contratação, vigência, histórico de agendamentos e utilização, frequência,
              cancelamentos/alterações e atendimento.
            </p>
            <p>
              3.3. Dados de pagamento: forma de pagamento, dados da transação, histórico de
              pagamentos e situação financeira do contrato. Dados completos de cartão podem ser
              processados por instituições financeiras ou plataformas especializadas.
            </p>
            <p>
              3.4. Dados de navegação e dispositivos: IP, data/horário de acesso, páginas visitadas,
              dispositivo, sistema operacional, navegador, cookies e parâmetros de campanhas.
            </p>
            <p>
              3.5. Dados de imagem: a Nook Gym poderá utilizar imagem de alunos para divulgação de
              seus serviços, nos termos e condições previstos no contrato firmado no momento da
              matrícula.
            </p>
            <p>
              3.6. Dados biométricos: a Nook Gym utiliza dados biométricos para controle de acesso às
              unidades, exclusivamente após a matrícula do aluno, como dado pessoal sensível sujeito
              aos requisitos da LGPD, nos termos e condições previstos no contrato de adesão.
            </p>
          </Section>

          <Section title="4. Dados pessoais sensíveis e informações de saúde">
            <p>
              A Nook Gym poderá tratar dados pessoais referentes à saúde e à condição física dos
              alunos, incluindo informações constantes de PAR-Q, declarações de saúde, limitações
              físicas, restrições à prática de exercícios, lesões, atestados, avaliações e outras
              informações fornecidas pelo titular ou por seu responsável legal, quando aplicável.
            </p>
            <p>
              Esses dados são considerados dados pessoais sensíveis pela LGPD e poderão ser tratados
              quando necessário para avaliar condições relacionadas à prática segura de exercícios,
              identificar limitações ou restrições informadas pelo titular, orientar medidas de
              segurança e cumprir obrigações legais ou regulatórias aplicáveis.
            </p>
            <p>
              O tratamento desses dados observará as hipóteses previstas no art. 11 da LGPD e será
              limitado às finalidades compatíveis com a prestação dos serviços e com a segurança dos
              alunos, sendo vedada sua utilização para finalidades incompatíveis com aquelas
              informadas nesta Política.
            </p>
          </Section>

          <Section title="5. Como os dados são coletados">
            <List
              items={[
                "Diretamente do titular",
                "Formulários de cadastro",
                "Agendamento de treino experimental",
                "Contratação dos serviços",
                "Canais oficiais de atendimento",
                "Navegação no site",
                "Cookies, pixels e tecnologias semelhantes",
                "Fornecedores/parceiros, quando permitido",
              ]}
            />
          </Section>

          <Section title="6. Para quais finalidades utilizamos os dados">
            <p>
              Os dados pessoais poderão ser tratados para as seguintes finalidades, conforme a
              natureza da relação mantida com o titular:
            </p>
            <List
              items={[
                "realização de cadastro e identificação;",
                "atendimento de solicitações, dúvidas e contatos;",
                "agendamento, confirmação e gerenciamento de treino experimental;",
                "análise e formalização da contratação;",
                "execução e administração do contrato de prestação de serviços;",
                "gerenciamento de planos, horários, agendamentos, frequência e utilização dos serviços;",
                "controle e autenticação de acesso às unidades;",
                "segurança dos alunos, colaboradores, prestadores e instalações;",
                "avaliação e acompanhamento de informações necessárias à prática segura de exercícios;",
                "processamento e gerenciamento de pagamentos;",
                "cobrança e gestão de situações financeiras relacionadas ao contrato;",
                "cumprimento de obrigações legais e regulatórias;",
                "exercício regular de direitos em processos judiciais, administrativos ou arbitrais;",
                "prevenção de fraudes, abusos e utilizações indevidas dos serviços;",
                "comunicação relacionada à prestação dos serviços, incluindo avisos, confirmações, alterações, cobranças e informações operacionais;",
                "envio de comunicações institucionais, promocionais e publicitárias, quando houver fundamento jurídico aplicável e observadas as preferências do titular;",
                "análise e melhoria dos serviços, do atendimento e da experiência dos usuários;",
                "funcionamento, segurança e aprimoramento do site e de seus recursos tecnológicos;",
                "realização de pesquisas, análises estatísticas e estudos internos, preferencialmente mediante utilização de dados anonimizados ou agregados quando possível.",
              ]}
            />
          </Section>

          <Section title="7. Bases legais">
            <p>
              A Nook Gym tratará os dados pessoais de acordo com as hipóteses previstas na LGPD,
              utilizando a base legal adequada à finalidade e à natureza de cada tratamento. Entre as
              bases legais que poderão ser utilizadas estão:
            </p>
            <List
              items={[
                "cumprimento de obrigação legal ou regulatória;",
                "execução de contrato ou de procedimentos preliminares relacionados ao contrato;",
                "exercício regular de direitos em processo judicial, administrativo ou arbitral;",
                "legítimo interesse da Nook Gym ou de terceiros, quando aplicável e desde que respeitados os direitos e liberdades fundamentais do titular;",
                "consentimento do titular, quando necessário;",
                "proteção da vida ou da incolumidade física do titular ou de terceiros, quando aplicável.",
              ]}
            />
            <p>
              O consentimento será utilizado somente quando essa for a hipótese legal adequada ao
              tratamento e poderá ser revogado pelo titular, observadas as condições previstas na
              LGPD. No caso de dados pessoais sensíveis, inclusive dados biométricos e informações de
              saúde, o tratamento observará especificamente as hipóteses previstas no art. 11 da
              LGPD.
            </p>
          </Section>

          <Section title="8. Cookies e tecnologias semelhantes">
            <p>
              A Nook Gym poderá utilizar cookies, pixels, tags e tecnologias semelhantes para garantir
              o funcionamento adequado do site, compreender como os usuários interagem com suas
              páginas, medir resultados de campanhas e, quando aplicável, realizar publicidade
              direcionada. Os cookies poderão ser classificados, conforme sua finalidade, em:
            </p>
            <List
              items={[
                "Cookies necessários: essenciais para o funcionamento, segurança e disponibilização de determinadas funcionalidades do site;",
                "Cookies analíticos: utilizados para compreender a utilização do site, identificar padrões de navegação e melhorar sua estrutura e conteúdo;",
                "Cookies de publicidade e marketing: utilizados para medir campanhas, personalizar conteúdos publicitários e compreender a interação dos usuários com anúncios.",
              ]}
            />
            <p>
              A Nook Gym poderá utilizar ferramentas e serviços de terceiros, como Google Analytics,
              Google Tag Manager, Google Ads e Meta Pixel, conforme as configurações efetivamente
              implementadas no site.
            </p>
            <p>
              Quando aplicável, cookies não essenciais serão utilizados de acordo com as preferências
              manifestadas pelo usuário por meio do mecanismo de gerenciamento de cookies
              disponibilizado no site.
            </p>
            <p>
              O usuário poderá, ainda, gerenciar determinadas preferências por meio das configurações
              de seu navegador, sem prejuízo de que a desativação de determinados cookies possa
              afetar funcionalidades do site. As informações coletadas por tecnologias de terceiros
              também estarão sujeitas às respectivas políticas de privacidade desses fornecedores.
            </p>
            <button
              type="button"
              onClick={openCookiePreferences}
              className="inline-flex h-11 items-center rounded-sm border border-border px-5 text-[0.72rem] font-semibold uppercase tracking-[0.16em] text-foreground hover:border-nook-green hover:text-nook-green"
            >
              Gerenciar preferências de cookies
            </button>
          </Section>

          <Section title="9. Compartilhamento de dados">
            <p>A Nook Gym não comercializa dados pessoais.</p>
            <p>
              Os dados poderão ser compartilhados, quando necessário e de acordo com a finalidade do
              tratamento, com fornecedores e prestadores de serviços que auxiliem a Nook Gym em suas
              atividades, incluindo empresas de tecnologia, hospedagem, sistemas de gestão e
              agendamento, processamento de pagamentos, comunicação, marketing, segurança,
              contabilidade, suporte jurídico e demais serviços necessários à operação da empresa.
            </p>
            <p>
              Os dados também poderão ser compartilhados com autoridades públicas, órgãos reguladores
              ou entidades competentes quando houver obrigação legal, determinação judicial ou
              administrativa ou quando o compartilhamento for necessário ao exercício regular de
              direitos.
            </p>
            <p>
              Quando terceiros atuarem como operadores de dados pessoais em nome da Nook Gym, serão
              adotadas medidas contratuais e administrativas compatíveis com a legislação aplicável.
            </p>
          </Section>

          <Section title="10. Transferência internacional">
            <p>
              Alguns fornecedores e prestadores de serviços utilizados pela Nook Gym poderão estar
              localizados no exterior ou utilizar infraestrutura tecnológica localizada fora do
              Brasil, o que poderá resultar em transferência internacional de dados pessoais.
            </p>
            <p>
              Quando houver transferência internacional, a Nook Gym adotará os mecanismos e requisitos
              previstos na LGPD e na regulamentação aplicável, buscando assegurar nível adequado de
              proteção aos dados pessoais tratados.
            </p>
          </Section>

          <Section title="11. Armazenamento e retenção">
            <p>
              Os dados pessoais serão armazenados pelo período necessário ao cumprimento das
              finalidades para as quais foram coletados e, quando aplicável, durante a vigência da
              relação contratual.
            </p>
            <p>
              Após o encerramento da finalidade ou da relação contratual, os dados poderão ser
              eliminados ou anonimizados, ressalvadas as hipóteses em que sua conservação seja
              necessária ou autorizada pela legislação, inclusive para:
            </p>
            <List
              items={[
                "cumprimento de obrigação legal ou regulatória;",
                "exercício regular de direitos;",
                "cumprimento de obrigações contratuais ou legais;",
                "prevenção e detecção de fraudes e ilícitos;",
                "atendimento a determinações de autoridades competentes.",
              ]}
            />
            <p>
              Os períodos de retenção poderão variar de acordo com a natureza do dado, sua finalidade
              e as obrigações legais aplicáveis.
            </p>
          </Section>

          <Section title="12. Segurança da informação">
            <p>
              A Nook Gym adota medidas técnicas e administrativas razoáveis e compatíveis com a
              natureza dos dados tratados para proteger os dados pessoais contra acessos não
              autorizados e situações acidentais ou ilícitas de destruição, perda, alteração,
              comunicação ou difusão.
            </p>
            <p>
              O acesso aos dados pessoais será limitado às pessoas que necessitem dessas informações
              para o desempenho de suas atividades, observadas as medidas de segurança e
              confidencialidade aplicáveis.
            </p>
            <p>
              Em caso de incidente de segurança envolvendo dados pessoais, a Nook Gym adotará as
              providências previstas na LGPD e na regulamentação aplicável, inclusive quanto à
              avaliação da necessidade de comunicação à Autoridade Nacional de Proteção de Dados
              (ANPD) e aos titulares, quando cabível.
            </p>
          </Section>

          <Section title="13. Direitos dos titulares">
            <p>
              Nos termos da LGPD e observadas as limitações e condições previstas na legislação, o
              titular poderá solicitar:
            </p>
            <List
              items={[
                "confirmação da existência de tratamento;",
                "acesso aos dados pessoais;",
                "correção de dados incompletos, inexatos ou desatualizados;",
                "anonimização, bloqueio ou eliminação de dados desnecessários, excessivos ou tratados em desconformidade com a legislação;",
                "portabilidade dos dados, observadas as regulamentações aplicáveis;",
                "informação sobre as entidades públicas e privadas com as quais os dados foram compartilhados;",
                "informação sobre a possibilidade de não fornecer consentimento e sobre as consequências dessa negativa, quando aplicável;",
                "revogação do consentimento, quando o tratamento estiver baseado nessa hipótese;",
                "oposição ao tratamento, nas hipóteses previstas na LGPD;",
                "demais direitos previstos na legislação aplicável.",
              ]}
            />
          </Section>

          <Section title="14. Como exercer os direitos">
            <p>
              Pelo e-mail{" "}
              <a href="mailto:contato@nookgym.com.br" className="text-nook-green hover:underline">
                contato@nookgym.com.br
              </a>
              . A identidade do solicitante poderá ser confirmada antes do atendimento.
            </p>
          </Section>

          <Section title="15. Crianças e adolescentes">
            <p>
              A Nook Gym poderá prestar serviços a crianças e adolescentes, observando, em qualquer
              situação, o princípio do melhor interesse e as disposições da LGPD e da legislação
              aplicável.
            </p>
            <p>
              No tratamento de dados pessoais de crianças, a Nook Gym observará as regras específicas
              previstas na LGPD, inclusive aquelas relativas ao consentimento de pelo menos um dos
              pais ou responsável legal, quando essa for a hipótese legal aplicável.
            </p>
            <p>
              No caso de adolescentes, o tratamento observará o melhor interesse do adolescente, as
              disposições da LGPD e as demais normas aplicáveis, não sendo automaticamente exigido
              consentimento parental para todo e qualquer tratamento exclusivamente em razão da idade.
            </p>
            <p>
              Quando necessário, a Nook Gym poderá solicitar documentos ou informações destinados a
              confirmar a identidade e a representação do responsável legal, bem como a autorização
              necessária para a contratação e utilização dos serviços.
            </p>
            <p>
              A Nook Gym buscará limitar a coleta de dados de crianças e adolescentes ao mínimo
              necessário para as finalidades legítimas relacionadas à prestação dos serviços,
              segurança, cumprimento de obrigações legais e demais finalidades informadas nesta
              Política.
            </p>
          </Section>

          <Section title="16. Links para terceiros">
            <p>
              O site pode linkar redes sociais, mensageria, mapas e sistemas de agendamento, sujeitos
              às políticas desses terceiros.
            </p>
          </Section>

          <Section title="17. Alterações desta Política">
            <p>
              Esta Política pode ser atualizada periodicamente, com a data de atualização indicada na
              página.
            </p>
          </Section>

          <Section title="18. Contato">
            <p>
              NOOK GYM ACADEMIA LTDA | CNPJ: 55.440.324/0001-32 | Rua Luiza Nogueira Gonçalves Prof.,
              nº 00350, Blc 1, Lojas 102/103/121/122, Recreio dos Bandeirantes, Rio de Janeiro/RJ, CEP
              22.790-465 | E-mail de privacidade:{" "}
              <a href="mailto:contato@nookgym.com.br" className="text-nook-green hover:underline">
                contato@nookgym.com.br
              </a>{" "}
              | Última atualização: {LAST_UPDATE}
            </p>
          </Section>
        </div>
      </div>
    </section>
  );
}
